import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import Select from "react-select";
import Breadcrumbs from "../../../../common-elements/breadcrumbs/Breadcrumbs";
import {
  ComposeMessage,
  EnterYourMessages,
  Send,
} from "../../../../utils/Constant";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { dynamicImage } from "../../../../service";

const roleOptions = [
  { value: "sales", label: "Sales" },
  { value: "developers", label: "Developers" },
  { value: "finance", label: "Finance" },
  { value: "admins", label: "Admins" },
  { value: "managers", label: "Managers" },
];

const notificationTypeOptions: {
  value: "warning" | "info" | "success" | "error";
  label: string;
}[] = [
  { value: "warning", label: "Warning" },
  { value: "info", label: "Info" },
  { value: "success", label: "Success" },
  { value: "error", label: "Error" },
];

const userData: {
  [key: string]: {
    value: string;
    label: string;
    role: string;
    avatar?: string;
  }[];
} = {
  sales: [
    { value: "all-sales", label: "All (Sales)", role: "sales" },
    {
      value: "john",
      label: "John Doe (Sales)",
      role: "sales",
      avatar: "dashboard-8/user3.png",
    },
    {
      value: "jane",
      label: "Jane Smith (Sales)",
      role: "sales",
      avatar: "dashboard-8/user3.png",
    },
  ],
  developers: [
    { value: "all-developers", label: "All (Developers)", role: "developers" },
    {
      value: "alice",
      label: "Alice Johnson (Developers)",
      role: "developers",
      avatar: "dashboard-8/user3.png",
    },
    {
      value: "bob",
      label: "Bob Wilson (Developers)",
      role: "developers",
      avatar: "dashboard-8/user3.png",
    },
  ],
  finance: [
    { value: "all-finance", label: "All (Finance)", role: "finance" },
    {
      value: "emma",
      label: "Emma Brown (Finance)",
      role: "finance",
      avatar: "dashboard-8/user3.png",
    },
    {
      value: "mike",
      label: "Mike Davis (Finance)",
      role: "finance",
      avatar: "dashboard-8/user3.png",
    },
  ],
  admins: [
    { value: "all-admins", label: "All (Admins)", role: "admins" },
    {
      value: "sarah",
      label: "Sarah Adams (Admins)",
      role: "admins",
      avatar: "dashboard-8/user3.png",
    },
  ],
  managers: [
    { value: "all-managers", label: "All (Managers)", role: "managers" },
    {
      value: "david",
      label: "David Lee (Managers)",
      role: "managers",
      avatar: "dashboard-8/user3.png",
    },
  ],
};

interface AddNewPushNotification {
  recipientRoles: string;
  title: string;
  message: string;
  notificationType: "warning" | "info" | "success" | "error";
}

const ComposePushNotificationPage: React.FC = () => {
  const [pushNotificationValidation, setPushNotificationValidation] =
    useState(false);
  const [selectedRoles, setSelectedRoles] = useState<typeof roleOptions>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
  } = useForm<AddNewPushNotification>({
    defaultValues: {
      notificationType: "info",
    },
  });

  const onSubmit: SubmitHandler<AddNewPushNotification> = (data) => {
    if (
      data.recipientRoles &&
      data.title &&
      data.message &&
      data.notificationType
    ) {
      console.log("Push Notification data:", data);
      reset();
      setValue("title", "");
      setValue("message", "");
      setValue("notificationType", "info");
      setPushNotificationValidation(false);
      setSelectedRoles([]);
      setSelectedUsers([]);
      navigate(`${process.env.PUBLIC_URL}/communication/push-notification`);
    } else {
      setPushNotificationValidation(true);
    }
  };

  const handleRoleChange = (selectedOptions: any) => {
    setSelectedRoles(selectedOptions || []);
    setSelectedUsers([]);
    setValue("recipientRoles", "");
  };

  const handleUserChange = (selectedOptions: any) => {
    let newSelectedUsers = selectedOptions || [];

    const selectedValues = newSelectedUsers.map((opt: any) => opt.value);

    newSelectedUsers = newSelectedUsers.filter((user: any) => {
      if (user.value.startsWith("all-")) {
        return true;
      }
      const roleOfUser = user.role;
      const allOptionForRole = `all-${roleOfUser}`;
      return !selectedValues.includes(allOptionForRole);
    });

    const finalSelection = newSelectedUsers.filter((user: any) => {
      if (!user.value.startsWith("all-")) {
        return true;
      }
      const roleOfAll = user.role;
      return !newSelectedUsers.some(
        (u: any) => u.role === roleOfAll && !u.value.startsWith("all-")
      );
    });

    setSelectedUsers(finalSelection);

    const roles = finalSelection.map((user: any) => {
      if (user.value.startsWith("all-")) {
        return user.role;
      }
      return user.value;
    });
    setValue("recipientRoles", Array.from(new Set(roles)).join(", "));
  };

  const getSelectableUsers = () => {
    if (!selectedRoles.length) {
      return [];
    }

    const userOptions = selectedRoles.flatMap((role) => userData[role.value]);

    userOptions.sort((a, b) => {
      const aIsAll = a.value.startsWith("all-");
      const bIsAll = b.value.startsWith("all-");

      if (aIsAll && !bIsAll) return -1;
      if (!aIsAll && bIsAll) return 1;
      return a.label.localeCompare(b.label);
    });

    const selectedValues = selectedUsers.map((u) => u.value);

    return userOptions.map((user) => {
      const isAllForRoleSelected = selectedValues.includes(`all-${user.role}`);
      const areIndividualUsersInRoleSelected = selectedValues.some(
        (v) =>
          !v.startsWith("all-") &&
          userData[user.role]?.some((u) => u.value === v)
      );

      let isDisabled = false;
      if (user.value.startsWith("all-")) {
        isDisabled = areIndividualUsersInRoleSelected;
      } else {
        isDisabled = isAllForRoleSelected;
      }

      return {
        ...user,
        isDisabled,
      };
    });
  };

  return (
    <div className="page-body">
      <Breadcrumbs
        mainTitle={ComposeMessage}
        parents={[
          {
            label: "Communication",
            link: `${process.env.PUBLIC_URL}/push-notification/letterbox`,
          },
          {
            label: "Push Notification",
            link: `${process.env.PUBLIC_URL}/communication/push-notification`,
          },
        ]}
      />
      <Container fluid>
        <Card>
          <CardBody>
            <Form
              className="needs-validation"
              onSubmit={handleSubmit(onSubmit)}
            >
              <FormGroup>
                <Row>
                  <Col sm="2">
                    <Label className="col-form-label">Role:</Label>
                  </Col>
                  <Col sm="10">
                    <Select
                      options={roleOptions}
                      isMulti
                      isClearable
                      value={selectedRoles}
                      onChange={handleRoleChange}
                      className="basic-multi-select"
                      classNamePrefix="select"
                    />
                    {pushNotificationValidation && !selectedRoles.length && (
                      <div className="invalid-feedback">
                        Please select at least one role
                      </div>
                    )}
                  </Col>
                </Row>
              </FormGroup>
              <FormGroup>
                <Row>
                  <Col sm="2">
                    <Label className="col-form-label">To:</Label>
                  </Col>
                  <Col sm="10">
                    <Select
                      isMulti
                      isClearable
                      isDisabled={!selectedRoles.length}
                      options={getSelectableUsers()}
                      value={selectedUsers}
                      onChange={handleUserChange}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      formatOptionLabel={(option: any) => (
                        <div className="d-flex align-items-center">
                          {option.avatar && (
                            <img
                              src={dynamicImage(option.avatar)}
                              alt={option.label}
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                marginRight: 8,
                              }}
                            />
                          )}
                          <span>{option.label}</span>
                        </div>
                      )}
                    />
                  </Col>
                </Row>
              </FormGroup>
              <FormGroup>
                <Row>
                  <Col sm="2">
                    <Label className="col-form-label">Notification Type:</Label>
                  </Col>
                  <Col sm="10">
                    <Select
                      options={notificationTypeOptions}
                      isClearable={false}
                      onChange={(option) =>
                        setValue(
                          "notificationType",
                          option ? option.value : "info"
                        )
                      }
                      className="basic-select"
                      classNamePrefix="select"
                    />
                    {pushNotificationValidation && errors.notificationType && (
                      <div className="invalid-feedback">
                        Please select a notification type
                      </div>
                    )}
                  </Col>
                </Row>
              </FormGroup>
              <FormGroup>
                <Row>
                  <Col sm="2">
                    <Label className="col-form-label">Title:</Label>
                  </Col>
                  <Col sm="10">
                    <Input
                      type="text"
                      {...register("title", {
                        required: true,
                        maxLength: 50,
                      })}
                      placeholder="Enter notification title"
                      className={`form-control ${
                        pushNotificationValidation && errors.title
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    {pushNotificationValidation && errors.title && (
                      <div className="invalid-feedback">
                        {errors.title.type === "required"
                          ? "Title is required"
                          : "Title must be 50 characters or less"}
                      </div>
                    )}
                  </Col>
                </Row>
              </FormGroup>
              <FormGroup>
                <Row>
                  <Col sm="2">
                    <Label className="col-form-label">Message:</Label>
                  </Col>
                  <Col sm="10">
                    <Input
                      type="textarea"
                      rows="4"
                      {...register("message", {
                        required: true,
                        maxLength: 160,
                      })}
                      placeholder={EnterYourMessages}
                      className={`form-control ${
                        pushNotificationValidation && errors.message
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    {pushNotificationValidation && errors.message && (
                      <div className="invalid-feedback">
                        {errors.message.type === "required"
                          ? "Message is required"
                          : "Message must be 160 characters or less"}
                      </div>
                    )}
                  </Col>
                </Row>
              </FormGroup>
              <div className="mt-3 d-flex justify-content-end">
                <Button
                  color="secondary"
                  type="button"
                  onClick={() =>
                    navigate(
                      `${process.env.PUBLIC_URL}/communication/push-notification`
                    )
                  }
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  className="ms-2"
                  onClick={() => setPushNotificationValidation(true)}
                >
                  {Send}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ComposePushNotificationPage;
